import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, useIntl } from 'react-intl';
import { getFormattedAccountType } from 'shared-components/src/utils/cardholders';
// Import Global Components
import Card from '../../../../../../../components/Card';
import Title from '../../../../../../../components/Title';
// Import Component Styles
import {
  DetailsCard,
  DetailRow,
  DetailLabel,
  DetailValue,
  TypeBadge,
} from '../../../styles';

const AccountDetailsCard = ({ accountDetails, page }) => {
  const intl = useIntl();

  // Format status for display
  const formatStatus = status => {
    const statusMap = {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
    };
    return statusMap[status] || status;
  };

  return (
    <Card>
      <Title
        title={
          accountDetails.name ||
          intl.messages[`${page}-title`] ||
          'External Account'
        }
        titleFor="details"
        page={page}
      />
      <DetailsCard>
        <DetailRow>
          <DetailLabel>
            {intl.messages[`${page}-contact-name-label`] || 'Contact Name'}
          </DetailLabel>
          <DetailValue>{accountDetails.name}</DetailValue>
        </DetailRow>
        {accountDetails?.etransfer?.name && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-name-label`] || 'Name'}
            </DetailLabel>
            <DetailValue>{accountDetails?.etransfer?.name}</DetailValue>
          </DetailRow>
        )}
        {accountDetails?.etransfer?.email && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-email-label`] || 'Email'}
            </DetailLabel>
            <DetailValue>{accountDetails?.etransfer?.email}</DetailValue>
          </DetailRow>
        )}

        {accountDetails?.etransfer?.phone && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-phone-label`] || 'Phone'}
            </DetailLabel>
            <DetailValue>{accountDetails?.etransfer?.phone}</DetailValue>
          </DetailRow>
        )}

        {/* Card Details */}
        {accountDetails?.card?.name && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-cardholder-name-label`] || 'Cardholder Name'}
            </DetailLabel>
            <DetailValue>{accountDetails.card.name}</DetailValue>
          </DetailRow>
        )}

        {accountDetails?.card?.last_four_digits && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-card-number-label`] || 'Card Number'}
            </DetailLabel>
            <DetailValue>**** **** **** {accountDetails.card.last_four_digits}</DetailValue>
          </DetailRow>
        )}

        {accountDetails?.card?.expiry_month && accountDetails?.card?.expiry_year && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-card-expiry-label`] || 'Expiry Date'}
            </DetailLabel>
            <DetailValue>
              {accountDetails.card.expiry_month}/{accountDetails.card.expiry_year}
            </DetailValue>
          </DetailRow>
        )}

        {accountDetails?.card?.address_line1 && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-address-label`] || 'Address'}
            </DetailLabel>
            <DetailValue>
              {accountDetails.card.address_line1}
              {accountDetails.card.address_line2 && `, ${accountDetails.card.address_line2}`}
              <br />
              {accountDetails.card.address_city}, {accountDetails.card.address_state}{' '}
              {accountDetails.card.address_postal_code}
              <br />
              {accountDetails.card.address_country}
            </DetailValue>
          </DetailRow>
        )}

        <DetailRow>
          <DetailLabel>
            {intl.messages[`${page}-type-label`] || 'Type'}
          </DetailLabel>
          <DetailValue>
            <TypeBadge $type={accountDetails.type}>
              {getFormattedAccountType(accountDetails.type)}
            </TypeBadge>
          </DetailValue>
        </DetailRow>

        {accountDetails.status && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-status-label`] || 'Status'}
            </DetailLabel>
            <DetailValue>{formatStatus(accountDetails.status)}</DetailValue>
          </DetailRow>
        )}

        <DetailRow>
          <DetailLabel>
            {intl.messages[`${page}-created-label`] || 'Created'}
          </DetailLabel>
          <DetailValue>
            <FormattedDate value={accountDetails.created_at} />
          </DetailValue>
        </DetailRow>

        {accountDetails.updated_at && (
          <DetailRow>
            <DetailLabel>
              {intl.messages[`${page}-updated-label`] || 'Last Updated'}
            </DetailLabel>
            <DetailValue>
              <FormattedDate value={accountDetails.updated_at} />
            </DetailValue>
          </DetailRow>
        )}
      </DetailsCard>
    </Card>
  );
};

AccountDetailsCard.propTypes = {
  accountDetails: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    etransfer: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }),
    card: PropTypes.shape({
      name: PropTypes.string,
      last_four_digits: PropTypes.string,
      expiry_month: PropTypes.string,
      expiry_year: PropTypes.string,
      address_line1: PropTypes.string,
      address_line2: PropTypes.string,
      address_city: PropTypes.string,
      address_state: PropTypes.string,
      address_country: PropTypes.string,
      address_postal_code: PropTypes.string,
    }),
  }).isRequired,
  page: PropTypes.string.isRequired,
};

export default AccountDetailsCard;
